import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en, { Dict } from './locales/en';
import uz from './locales/uz';
import ru from './locales/ru';

export const LOCALES = ['en', 'uz', 'ru'] as const;
export type Locale = (typeof LOCALES)[number];

const DICTS: Record<Locale, Dict> = { en, uz, ru };

/** Short code shown on the header's language button. */
export const LOCALE_CODE: Record<Locale, string> = { en: 'EN', uz: 'UZ', ru: 'RU' };

/**
 * English is the default and is *not* inferred from the browser: this is a
 * corporate site read by partners abroad as often as locally, and a visitor who
 * lands on an unexpected language cannot always find their way back. A choice
 * the visitor makes explicitly is remembered; no choice means English.
 */
export const DEFAULT_LOCALE: Locale = 'en';

const STORAGE_KEY = 'vaqtly.locale';

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

function readStored(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : DEFAULT_LOCALE;
  } catch {
    /* Private mode or a blocked storage partition — fall back rather than crash. */
    return DEFAULT_LOCALE;
  }
}

type LocaleContextValue = {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: Dict;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStored);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* Not being able to remember the choice must not stop it taking effect. */
    }
  }, []);

  /* Keeps the document language in step, so screen readers switch voice and the
     browser offers to translate the right thing. */
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t: DICTS[locale] }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

function useLocaleContext() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useT/useLocale must be used inside <LocaleProvider>');
  return ctx;
}

/** The active dictionary. */
export function useT(): Dict {
  return useLocaleContext().t;
}

/** The active locale and the setter behind the header's language menu. */
export function useLocale() {
  const { locale, setLocale } = useLocaleContext();
  return { locale, setLocale };
}
